import type { SurveySummary } from '@/state/surveys/useSurveyResults';
import { useParams, useNavigate } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { cn } from 'helpers/cn';
import { Button } from '@/components/ui/button';
import { useSurvey } from '@/state/surveys/useSurvey';
import { useSurveyResults } from '@/state/surveys/useSurveyResults';
import { QuestionResults } from './QuestionResults';
import { ResponsesFeed } from './ResponsesFeed';
import { ResponsesOverTime } from './ResponsesOverTime';
import { CHIP, PANEL, PANEL_HEAD, PANEL_TITLE, SENTIMENT_STYLES } from './result-styles';
import { VariantBreakdown } from './VariantBreakdown';

/** The headline number means something different per instrument, so it is labelled per instrument. */
const scoreCaption = (summary: SurveySummary): string => {
  // Only called for NPS, CSAT and CES: a Custom survey has no single score.
  if (summary.type === 'NPS') return 'Net Promoter Score (−100 to 100)';
  if (summary.type === 'CSAT') return 'Average satisfaction (1–5)';
  return 'Average effort (1–7)';
};

type StatProps = { label: string; value: string; hint?: string };

const Stat = ({ label, value, hint }: StatProps) => (
  <div className="bg-panel2 border-line rounded-card border p-3.5">
    <p className="micro-label m-0">{label}</p>
    <p className="metric-value text-foreground m-0 mt-2">{value}</p>
    {hint && <p className="supporting m-0 mt-1.5">{hint}</p>}
  </div>
);

/**
 * `embedded` drops the local back link and title, for when this renders as a tab
 * of the survey details hub — which already carries both. Standalone rendering
 * is kept so the screen stays usable on its own.
 */
type SurveyResultsProps = { embedded?: boolean };

/** Why nobody has seen it yet, by status: only a Running survey is waiting for a matching visitor. */
const NOT_SHOWN: Record<string, string> = {
  Draft: 'Not shown to anyone yet. Start the survey to put it on the website.',
  Running: 'Not shown to anyone yet. It shows once a visitor matches its targeting.',
  Ended: 'It ended before anyone was shown it.',
};

export const SurveyResults = ({ embedded = false }: SurveyResultsProps) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const surveyId = Number(id);
  const { survey } = useSurvey(surveyId);
  const { summary, isLoading, questions } = useSurveyResults(surveyId);
  const labelOf = (questionId: number) =>
    survey?.questions.find((question) => question.id === questionId)?.label ?? 'A question since removed';

  if (isLoading) return null;
  // A failed summary used to leave the tab blank for good, which reads as "no
  // data" rather than "something went wrong".
  if (!summary) {
    return (
      <section className={cn(PANEL, 'px-6 py-12 text-center')}>
        <p className="text-foreground m-0 text-body font-medium">Results could not be loaded</p>
        <p className="supporting mt-1">Try again in a moment.</p>
      </section>
    );
  }

  const hasResponses = summary.answered > 0;
  const isCustom = summary.type === 'Custom';

  return (
    <main className="flex flex-col gap-[14px]">
      {!embedded && (
        <section className={cn(PANEL, 'flex items-center gap-3 px-4 py-3.5')}>
          <Button
            variant="ghost"
            className="text-dim hover:text-foreground hover:bg-hover h-8 w-8 rounded-lg p-0"
            onClick={() => navigate('/surveys/library')}
            aria-label="Back to library"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-foreground m-0 truncate text-title font-medium leading-tight">
              {survey?.name ?? 'Survey results'}
            </h1>
            <p className="micro-label mt-1">{summary.type}</p>
          </div>
        </section>
      )}

      {!hasResponses && (
        <section className={cn(PANEL, 'px-6 py-12 text-center')} data-first-run>
          <p className="text-foreground m-0 text-body font-medium">No responses yet</p>
          <p className="supporting mt-1">
            {summary.impressions > 0
              ? `Shown ${summary.impressions} times so far. Scores appear once someone answers.`
              : NOT_SHOWN[survey?.status ?? 'Draft']}
          </p>
        </section>
      )}

      {hasResponses && (
        <>
          <section className={PANEL} data-tour="survey-results-summary">
            <div className={PANEL_HEAD}>
              <h2 className={PANEL_TITLE}>Summary</h2>
            </div>
            <div className={cn('grid gap-3 p-3 sm:grid-cols-2', isCustom ? 'lg:grid-cols-3' : 'lg:grid-cols-4')}>
              {/* A Custom survey has no single score; its scores are in By question. */}
              {!isCustom && (
                <Stat
                  label="Score"
                  value={summary.score === null ? 'None yet' : String(summary.score)}
                  hint={scoreCaption(summary)}
                />
              )}
              <Stat label="Responses" value={String(summary.answered)} hint={`${summary.impressions} shown`} />
              <Stat
                label="Response rate"
                // Null means nothing has been shown, which is not the same as 0%.
                value={summary.responseRate === null ? 'Not shown yet' : `${summary.responseRate}%`}
              />
              <Stat label="Comments" value={String(summary.sentiment.comments)} hint="Free-text answers" />
            </div>
          </section>

          {summary.breakdown && (
            <section className={PANEL} data-tour="survey-results-distribution">
              <div className={PANEL_HEAD}>
                <h2 className={PANEL_TITLE}>Distribution</h2>
              </div>
              <div className="grid gap-3 p-3 sm:grid-cols-3">
                {Object.entries(summary.breakdown).map(([label, value]) => (
                  <div key={label} className="bg-panel2 border-line rounded-card border p-3">
                    <p className="micro-label m-0">{label}</p>
                    <p className="text-foreground num m-0 mt-1.5 text-title">
                      {value}
                      <span className="text-dim ml-1.5">{Math.round((value / summary.answered) * 100)}%</span>
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {questions && questions.length > 0 && <QuestionResults questions={questions} />}

          <VariantBreakdown surveyId={surveyId} linkedExperimentId={survey?.experimentId ?? null} />

          <ResponsesOverTime surveyId={surveyId} scoreCaption={isCustom ? undefined : scoreCaption(summary)} />

          {(summary.sentiment.enabled ||
            summary.sentiment.positive + summary.sentiment.neutral + summary.sentiment.negative > 0) && (
            <section className={PANEL} data-tour="survey-results-sentiment">
              <div className={PANEL_HEAD}>
                <h2 className={PANEL_TITLE}>Sentiment</h2>
                {summary.sentiment.enabled && summary.sentiment.unclassified > 0 && (
                  <span className="micro-label">{summary.sentiment.unclassified} awaiting classification</span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 p-4">
                {(['positive', 'neutral', 'negative'] as const).map((key) => (
                  <span key={key} className={cn(CHIP, 'gap-1.5 px-2.5 py-1', SENTIMENT_STYLES[key])}>
                    {key}
                    <span className="num text-micro">{summary.sentiment[key]}</span>
                  </span>
                ))}
              </div>
            </section>
          )}

          <ResponsesFeed surveyId={surveyId} labelOf={labelOf} />
        </>
      )}
    </main>
  );
};
